import { OverviewPanelData } from '@app/areas/dashboard/components/dashboard-overview-panel/dashboard-overview-panel.component';
import { ColorsEnum } from '@shared/enums/colors.enum';
import { ViewCardConfig } from '@app/areas/dashboard/components/view-card/view-card.component';
import {
  CustomChartJSData,
  DashboardChartJSData,
} from '@app/areas/dashboard/components/dashboard-graph/dashboard-graph.component';
import { DailySaleOverview } from '@shared/models/daily-sale-overview.model';
import moment from 'moment-timezone';
import { DashboardOverviewData } from '@app/areas/dashboard/components/abstract-dashboard-date-panel-view/abstract-dashboard-date-panel-view.component';
import { CurrencyPipe } from '@angular/common';
import { PortalTheme } from '@app/@shared/models';

export function getDailySaleOverviewData(
  dashboardOverviewData?: DashboardOverviewData,
  theme?: PortalTheme
): OverviewPanelData[] {
  const _currency = new CurrencyPipe('en-US');
  return [
    {
      header: 'TOTAL',
      subHeader: `${dashboardOverviewData?.TotalOrderCount ?? 0} Transactions`,
      body: `${_currency.transform(dashboardOverviewData?.TotalSpend ?? 0)}`,
      bodyColor: theme?.dashboard.sales ?? ColorsEnum.LIGHT_BLUE,
      footer: `${_currency.transform(dashboardOverviewData?.AverageSpend ?? 0)} Average`,
    },
    {
      header: 'MEMBER',
      subHeader: `${dashboardOverviewData?.TotalMemberOrderCount ?? 0} Transactions (${(
        dashboardOverviewData?.MemberChecks ?? 0
      ).toFixed(2)}%)`,
      body: `${_currency.transform(dashboardOverviewData?.MemberSpend ?? 0)}`,
      bodyColor: theme?.dashboard.loyalty ?? ColorsEnum.MOSSY_GREEN,
      footer: `${_currency.transform(dashboardOverviewData?.AverageMemberSpend ?? 0)}
          ${(dashboardOverviewData?.AverageMemberSpend ?? 0) > 1000 ? 'avg' : 'Average'}
          ${
            (dashboardOverviewData?.PercentageMemberSpend ?? 0) > 0
              ? ` | Loyalty Adds ${dashboardOverviewData?.PercentageMemberSpend.toFixed(0)}%`
              : ''
          }`,
    },
    {
      header: 'NON-MEMBER',
      subHeader: `${dashboardOverviewData?.TotalNonMemberOrderCount ?? 0} Transactions`,
      body: `${_currency.transform(dashboardOverviewData?.NonMemberSpend ?? 0)}`,
      bodyColor: theme?.dashboard.rewards ?? ColorsEnum.DOC_PURPLE,
      footer: `${_currency.transform(dashboardOverviewData?.AverageNonMemberSpend ?? 0)} Average`,
    },
    ...(dashboardOverviewData?.MemberAverageSecondsBetweenVisit !== 0
      ? [
          {
            header: 'VISIT FREQUENCY',
            subHeader: `Returning Members Visit`,
            body: `${dashboardOverviewData?.MemberAverageSecondsBetweenVisitLabel ?? '0'}`,
            bodyColor: theme?.dashboard.reviews ?? ColorsEnum.BLUE_GREY,
            footer:
              dashboardOverviewData?.MemberVisitMultiplier! < 1
                ? ''
                : `${dashboardOverviewData?.MemberVisitMultiplier ?? 0}x more than Non-Members`,
          },
        ]
      : []),
  ];
}

export function getDailyLoyaltyOverviewData(
  dashboardOverviewData?: DashboardOverviewData,
  theme?: PortalTheme
): OverviewPanelData[] {
  var averageWhenRedeeming = 0;
  if (
    dashboardOverviewData?.TotalOrdersWhenRedeemingCount &&
    dashboardOverviewData?.TotalOrdersWhenRedeemingCount !== 0
  ) {
    averageWhenRedeeming =
      dashboardOverviewData?.TotalAmountWhenRedeemingAmount / dashboardOverviewData?.TotalOrdersWhenRedeemingCount;
  }

  const _currency = new CurrencyPipe('en-US');
  return [
    {
      header: 'MEMBER CONVERSION',
      body: `${
        dashboardOverviewData?.EnrollmentConversion !== undefined && dashboardOverviewData?.EnrollmentConversion !== 0
          ? dashboardOverviewData.EnrollmentConversion.toFixed(2) + '%'
          : 'N/A'
      }`,
      footer: `${dashboardOverviewData?.LoyaltyTotalEnrollmentOrderCount ?? 0} Enrolled`,
      bodyColor: theme?.dashboard.sales ?? ColorsEnum.LIGHT_BLUE,
      subFooter: `${dashboardOverviewData?.TotalOpportunityCount ?? 0} Opportunities`,
    },
    {
      header: 'ENROLLMENT TRANSACTIONS',
      body: `${_currency.transform(dashboardOverviewData?.LoyaltyTotalEnrollmentSpendAmount ?? 0)}`,
      bodyColor: theme?.dashboard.loyalty ?? ColorsEnum.MOSSY_GREEN,
      footer: `${
        (dashboardOverviewData?.RewardsTotalSpend ?? 0) !== 0
          ? _currency.transform(dashboardOverviewData?.TotalDiscountOrderAmount ?? 0) + ' Enrollment Rewarded'
          : ''
      }`,
      subFooter: `${_currency.transform(dashboardOverviewData?.EnrollmentAverageSpend ?? 0)} Average Transaction`,
    },
    {
      header: 'REWARDS',
      body: `${_currency.transform(averageWhenRedeeming ?? 0)}`,
      bodyColor: theme?.dashboard.rewards ?? ColorsEnum.DOC_PURPLE,
      subBody: 'Average When Redeeming',
      footer: `${_currency.transform(dashboardOverviewData?.EarnedRewardsTotal ?? 0)} Earned`,
      subFooter: `${_currency.transform(dashboardOverviewData?.RewardsTotalSpend ?? 0)} Redeemed`,
    },
    ...((dashboardOverviewData?.IncreasedMemberValue ?? 0) > 0
      ? [
          {
            header: 'MEMBER VALUE',
            body: `${dashboardOverviewData?.IncreasedMemberValue ?? 0}% `,
            bodyColor: theme?.dashboard.reviews ?? ColorsEnum.BLUE_GREY,
            subBody: 'revenue increase',
            footer:
              (dashboardOverviewData?.MemberReturn ?? 0) < (dashboardOverviewData?.NonMemberReturn ?? 0)
                ? ''
                : `${dashboardOverviewData?.MemberReturn ?? 0}% of Members have repeat visits`,
            subFooter:
              (dashboardOverviewData?.MemberReturn ?? 0) < (dashboardOverviewData?.NonMemberReturn ?? 0)
                ? ''
                : `${dashboardOverviewData?.NonMemberReturn ?? 0}% of Non-Members have repeat visits`,
          },
        ]
      : []),
  ];
}

export function getLoyaltyProgramData(
  dashboardOverviewData?: DashboardOverviewData,
  theme?: PortalTheme
): ViewCardConfig[] {
  const rawPercentage =
    (1 - (dashboardOverviewData?.RewardsTotalOrderSpend ?? 0) / (dashboardOverviewData?.TotalSpend ?? 1)) * 100;
  const percentage = isNaN(rawPercentage) ? 0 : rawPercentage;
  const basePrice =
    (dashboardOverviewData?.MerchantAmountDue ?? 0) + (dashboardOverviewData?.RewardsTotalOrderSpend ?? 0);
  const roiValue = getWouldYouTradeFor(dashboardOverviewData);
  const showROI = roiValue > basePrice;
  const _currency = new CurrencyPipe('en-US');

  return [
    {
      header: `${_currency.transform(dashboardOverviewData?.TotalSpend ?? 0)}`,
      headerSubtext: '',
      subheader: 'SALES',
      text: `\u00A0`,
      backgroundColor: theme?.dashboard.sales ?? ColorsEnum.LIGHT_BLUE,
      pathToImg: '../../../../../assets/images/line-graph.png',
    },
    {
      header: `${dashboardOverviewData?.TotalEnrollmentOrderCount ?? 0}`,
      headerSubtext: 'Enrolled',
      subheader: 'LOYALTY',
      text: `${dashboardOverviewData?.EnrollmentCount ?? 0} Current `,
      backgroundColor: theme?.dashboard.loyalty ?? ColorsEnum.MOSSY_GREEN,
      pathToImg: '../../../../../assets/images/handshake.png',
    },
    {
      header: ` ${
        dashboardOverviewData?.RewardsTotalOrderSpend !== null &&
        (dashboardOverviewData?.RewardsTotalOrderSpend ?? 0) !== 0
          ? percentage.toFixed(2) + '%'
          : 'N/A'
      } `,
      headerSubtext: ` ${
        dashboardOverviewData?.RewardsTotalOrderSpend !== null &&
        (dashboardOverviewData?.RewardsTotalOrderSpend ?? 0) !== 0
          ? 'ROI'
          : ''
      }`,
      subheader: 'REWARDS',
      text: `${_currency.transform(dashboardOverviewData?.MemberSpend ?? 0)} Members Sales | ${_currency.transform(
        dashboardOverviewData?.RewardsTotalOrderSpend ?? 0
      )} Redeemed`,
      backgroundColor: theme?.dashboard.rewards ?? ColorsEnum.DOC_PURPLE,
      pathToImg: '../../../../../assets/images/thumbs-up.png',
    },
    {
      header: showROI
        ? `$${roiValue.toFixed(2)}`
        : `${
            !!dashboardOverviewData?.TotalQuestionAnswerCount
              ? getQuestionPercentageScore(dashboardOverviewData)
              : 'N/A'
          }`,
      headerSubtext: showROI ? `` : !!dashboardOverviewData?.TotalQuestionAnswerCount ? 'Average Score' : null,
      subheader: showROI ? `WOULD YOU TRADE $${basePrice.toFixed(2)} FOR` : 'REVIEWS',
      text: showROI ? `\u00A0` : `${dashboardOverviewData?.TotalQuestionAnswerCount ?? 0} Responses`,
      backgroundColor: ColorsEnum.BLUE_GREY,
      pathToImg: showROI ? '../../../../../assets/images/money.png' : '../../../../../assets/images/star.png',
    },
  ];
}

export function getQuestionPercentageScore(dashboardOverviewData?: DashboardOverviewData): string {
  if (!dashboardOverviewData) return 'N/A';

  const questionCount = dashboardOverviewData.TotalQuestionAnswerCount;
  const actualScore = dashboardOverviewData.TotalQuestionAnswerScore;

  // Return N/A if no questions were answered or if we have invalid data
  if (!questionCount || questionCount <= 0 || actualScore < 0) {
    return 'N/A';
  }

  const totalPossibleScore = questionCount * 5;
  const percentage = (actualScore / totalPossibleScore) * 100;

  // Ensure percentage doesn't exceed 100% due to data inconsistencies
  const clampedPercentage = Math.min(percentage, 100);

  return `${clampedPercentage.toFixed(2)}%`;
}

export function getPrefilledChartJSData(label: string, borderColor: string = ColorsEnum.LIGHT_BLUE): CustomChartJSData {
  return {
    label: label,
    data: [],
    fill: false,
    borderColor: borderColor,
    backgroundColor: borderColor,
    tension: 0.4,
    order: 0,
  };
}

export function getDashboardData(data?: DailySaleOverview[] | null, theme?: PortalTheme) {
  let dashboardOverviewData: DashboardOverviewData = {
    RewardsTotalOrderSpend: 0,
    RewardsTotalSpend: 0,
    TotalQuestionAnswerCount: 0,
    TotalQuestionAnswerScore: 0,
    TotalQuestionAnswerAverage: 0,
    TotalSpend: 0,
    AverageSpend: 0,
    NonMemberSpend: 0,
    AverageNonMemberSpend: 0,
    MemberSpend: 0,
    AverageMemberSpend: 0,
    PercentageMemberSpend: 0,
    MemberChecks: 0,
    EnrollmentCount: 0,
    EnrollmentConversion: 0,
    EnrollmentTotalSpend: 0,
    EnrollmentAverageSpend: 0,
    NewEnrollmentTotalSpend: 0,
    NewEnrollmentAverageTotalSpend: 0,
    EarnedRewardsTotal: 0,
    TotalMemberOrderCount: 0,
    TotalNonMemberOrderCount: 0,
    TotalEnrollmentOrderCount: 0,
    TotalOrderCount: 0,
    TotalOpportunityCount: 0,
    TotalEnrollmentCancelledCount: 0,
    TotalDiscountOrderAmount: 0,
    AverageAmountTotal: 0,
    TotalOrdersWhenRedeemingCount: 0,
    TotalAmountWhenRedeemingAmount: 0,
    MerchantAmountDue: 0,
    LoyaltyTotalEnrollmentOrderCount: 0,
    LoyaltyTotalEnrollmentSpendAmount: 0,
    MemberAverageSecondsBetweenVisit: 0,
    MemberAverageSecondsBetweenVisitLabel: '0',
    MemberVisitMultiplier: 0,
    IncreasedMemberValue: 0,
    MemberReturn: 0,
    NonMemberReturn: 0,
  };

  let dashboardChartData: DashboardChartJSData = {
    labels: [],
    datasets: [
      {
        ...getPrefilledChartJSData('Total Sales', theme?.dashboard.sales ?? ColorsEnum.LIGHT_BLUE),
      },
      {
        ...getPrefilledChartJSData('Member Sales', theme?.dashboard.loyalty ?? ColorsEnum.MOSSY_GREEN),
      },
      {
        ...getPrefilledChartJSData('Non-Member Sales', theme?.dashboard.rewards ?? ColorsEnum.BLUE_GREY),
      },
    ],
  };

  if (data) {
    for (let i = 0; i < data.length; i++) {
      // Set Overview data
      dashboardOverviewData.TotalSpend +=
        data[i].TotalNonMemberSpendAmount + data[i].TotalMemberSpendAmount + data[i].TotalEnrollmentSpendAmount;
      dashboardOverviewData.NonMemberSpend += data[i].TotalNonMemberSpendAmount;
      dashboardOverviewData.MemberSpend += data[i].TotalMemberSpendAmount + data[i].TotalEnrollmentSpendAmount;
      dashboardOverviewData.TotalOrderCount +=
        data[i].TotalNonMemberOrderCount + data[i].TotalMemberOrderCount + data[i].TotalEnrollmentOrderCount;
      dashboardOverviewData.TotalNonMemberOrderCount += data[i].TotalNonMemberOrderCount;
      dashboardOverviewData.TotalMemberOrderCount += data[i].TotalMemberOrderCount + data[i].TotalEnrollmentOrderCount;
      // dashboardOverviewData.EnrollmentConversion += data[i].TotalMemberSpendAmount + data[i].TotalEnrollmentSpendAmount;
      dashboardOverviewData.EnrollmentTotalSpend += data[i].TotalEnrollmentSpendAmount;
      dashboardOverviewData.NewEnrollmentTotalSpend += data[i].TotalEnrollmentSpendAmount;
      dashboardOverviewData.EarnedRewardsTotal += data[i].TotalRewardEarnedAmount;
      dashboardOverviewData.TotalEnrollmentOrderCount += data[i].TotalEnrollmentOrderCount;
      dashboardOverviewData.RewardsTotalSpend += data[i].TotalRewardSpentAmount;
      dashboardOverviewData.RewardsTotalOrderSpend += data[i].TotalRewardSpentOrderAmount;
      dashboardOverviewData.TotalQuestionAnswerCount += data[i].TotalQuestionAnswerCount;
      dashboardOverviewData.TotalQuestionAnswerScore += data[i].TotalQuestionAnswerScore;
      dashboardOverviewData.TotalOpportunityCount += data[i].TotalOpportunityCount;
      dashboardOverviewData.TotalEnrollmentCancelledCount = data[i].TotalEnrollmentCancelledCount;
      dashboardOverviewData.TotalDiscountOrderAmount += data[i].TotalDiscountOrderAmount;
      dashboardOverviewData.AverageAmountTotal += data[i].AverageAmountTotal;
      dashboardOverviewData.TotalOrdersWhenRedeemingCount += data[i].TotalOrdersWhenRedeemingCount;
      dashboardOverviewData.TotalAmountWhenRedeemingAmount += data[i].TotalAmountWhenRedeemingAmount;
      dashboardOverviewData.MerchantAmountDue += data[i].MerchantAmountDue;
      dashboardOverviewData.LoyaltyTotalEnrollmentOrderCount += data[i].LoyaltyTotalEnrollmentOrderCount;
      dashboardOverviewData.LoyaltyTotalEnrollmentSpendAmount += data[i].LoyaltyTotalEnrollmentSpendAmount;

      // Set Chart data
      dashboardChartData.labels.push(moment(data[i].DailyDate).format('MM-DD-YYYY'));
      // Insert Total Sales data
      dashboardChartData.datasets[0].data.push(
        data[i].TotalNonMemberSpendAmount + data[i].TotalMemberSpendAmount + data[i].TotalEnrollmentSpendAmount
      );
      // Insert Member Sales data
      dashboardChartData.datasets[1].data.push(data[i].TotalMemberSpendAmount + data[i].TotalEnrollmentSpendAmount);
      // Insert Non-Member Sales data
      dashboardChartData.datasets[2].data.push(data[i].TotalNonMemberSpendAmount);

      dashboardOverviewData.MemberAverageSecondsBetweenVisit = data[i].MemberAverageSecondsBetweenVisit;
      dashboardOverviewData.MemberVisitMultiplier = data[i].MemberVisitMultiplier;
      dashboardOverviewData.IncreasedMemberValue = data[i].IncreasedMemberValue;
      dashboardOverviewData.MemberReturn = data[i].MemberReturn;
      dashboardOverviewData.NonMemberReturn = data[i].NonMemberReturn;
      dashboardOverviewData.MemberAverageSecondsBetweenVisitLabel = data[i].MemberAverageSecondsBetweenVisitLabel;
    }

    for (const element of data) {
      if (element.EnrollmentCount > 0) {
        dashboardOverviewData.EnrollmentCount = element.EnrollmentCount;
        break;
      }
    }

    if (dashboardOverviewData.TotalOrderCount !== 0) {
      dashboardOverviewData.AverageSpend = dashboardOverviewData.TotalSpend / dashboardOverviewData.TotalOrderCount;
      dashboardOverviewData.MemberChecks =
        (dashboardOverviewData.TotalMemberOrderCount / dashboardOverviewData.TotalOrderCount) * 100;
    }
    if (dashboardOverviewData.TotalMemberOrderCount !== 0)
      dashboardOverviewData.AverageMemberSpend =
        dashboardOverviewData.MemberSpend / dashboardOverviewData.TotalMemberOrderCount;
    if (dashboardOverviewData.TotalNonMemberOrderCount !== 0)
      dashboardOverviewData.AverageNonMemberSpend =
        dashboardOverviewData.NonMemberSpend / dashboardOverviewData.TotalNonMemberOrderCount;

    if (dashboardOverviewData.TotalOpportunityCount > 0 && dashboardOverviewData.LoyaltyTotalEnrollmentOrderCount > 0) {
      dashboardOverviewData.EnrollmentConversion =
        (dashboardOverviewData.LoyaltyTotalEnrollmentOrderCount / dashboardOverviewData.TotalOpportunityCount) * 100;
    }

    if (dashboardOverviewData.LoyaltyTotalEnrollmentOrderCount) {
      dashboardOverviewData.EnrollmentAverageSpend =
        dashboardOverviewData.LoyaltyTotalEnrollmentSpendAmount /
        dashboardOverviewData.LoyaltyTotalEnrollmentOrderCount;
    }
    if (dashboardOverviewData.TotalQuestionAnswerCount !== 0) {
      dashboardOverviewData.TotalQuestionAnswerAverage =
        dashboardOverviewData.TotalQuestionAnswerScore / dashboardOverviewData.TotalQuestionAnswerCount;
    }

    dashboardOverviewData.PercentageMemberSpend = getPercentageMemberSpend(
      dashboardOverviewData.AverageNonMemberSpend,
      dashboardOverviewData.AverageMemberSpend
    );
  }

  return { dashboardOverviewData: dashboardOverviewData, dashboardChartData: dashboardChartData };
}

export function getPercentageMemberSpend(averageNonMemberSpend: number, averageMemberSpend: number): number {
  let percentageMemberSpend = 0;

  if (averageNonMemberSpend !== 0) {
    percentageMemberSpend = ((averageMemberSpend - averageNonMemberSpend) / averageNonMemberSpend) * 100;
  } else if (averageMemberSpend !== 0) {
    percentageMemberSpend = 100;
  }

  return percentageMemberSpend;
}

export function getWouldYouTradeFor(dashboardOverviewData?: DashboardOverviewData): number {
  var result = 0;

  if (dashboardOverviewData) {
    result =
      (Number(dashboardOverviewData.AverageMemberSpend.toFixed(2)) -
        Number(dashboardOverviewData.AverageNonMemberSpend.toFixed(2))) *
      Number(dashboardOverviewData.TotalMemberOrderCount.toFixed(2));
  }

  return result;
}
